'use client';
import React, { useRef, useEffect, useState } from 'react';

export default function FinalCTASection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0d0818 0%, #130a24 50%, #1a0d30 75%, #0d0818 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glow-orb-lavender"
          style={{ width: '700px', height: '700px', opacity: 0.4 }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glow-orb-gold"
          style={{ width: '500px', height: '500px', opacity: 0.25 }}
        />
      </div>

      {/* Sacred rings */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '600px', height: '600px', border: '1px solid rgba(201,168,76,0.10)', animation: 'rotateSlow 50s linear infinite' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '400px', height: '400px', border: '1px solid rgba(155,127,232,0.12)', animation: 'rotateSlow 35s linear infinite reverse' }}
        />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Decorative */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
            <span className="text-[#c9a84c] text-lg">✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
          </div>

          <h2
            className="font-serif text-4xl md:text-6xl font-bold mb-6 leading-tight"
            style={{
              background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 40%, #c9a84c 70%, #e8c96a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Ready to Begin Your Healing Journey?
          </h2>

          <p className="text-[#c4b5fd]/70 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Take the first step toward transformation. Whether you seek emotional healing, spiritual growth, or inner peace — your journey begins with a single decision.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <a
              href="https://cal.com/shruti-g6cvvv/30min?overlayCalendar=true"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-semibold text-sm tracking-widest uppercase transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 50%, #c9a84c 100%)',
                color: '#0d0818',
                boxShadow: '0 6px 40px rgba(201,168,76,0.4)',
              }}
            >
              ✨ Book Your Session
            </a>
            <a
              href="#courses"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-semibold text-sm tracking-widest uppercase transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(155,127,232,0.12)',
                border: '1.5px solid rgba(155,127,232,0.50)',
                color: '#c4b5fd',
                boxShadow: '0 4px 20px rgba(155,127,232,0.2)',
              }}
            >
              🌸 Join Our Courses
            </a>
          </div>

          {/* Trust line */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-[#c4b5fd]/40 text-xs tracking-widest uppercase">
            <span>✦ 10+ Years Experience</span>
            <span>✦ 1000+ Lives Transformed</span>
            <span>✦ Confidential Sessions</span>
            <span>✦ Online & In-Person</span>
          </div>
        </div>
      </div>
    </section>
  );
}
